import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Analytics Dashboard — KPI cards, charts, AI insights.
 * Shows actionable intelligence from the graph engine.
 */
export default function AnalyticsDashboard({ token }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/aif/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setData(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-sm text-[var(--aif-text-muted)] text-center py-8">{t('analytics.noData')}</p>;

  const kpi = data.kpi || {};
  const charts = data.charts || {};
  const insights = data.insights || [];

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3">
        <KpiCard label={t('analytics.totalHa')} value={`${kpi.total_ha || 0}`} unit="ha" color="green" />
        <KpiCard label={t('analytics.costPerHa')} value={`${kpi.cost_per_ha || 0}`} unit="EUR/ha" color="amber" />
        <KpiCard label={t('analytics.tasksCompleted')} value={`${kpi.tasks_completed || 0}`} unit="" color="blue" />
        <KpiCard label={t('analytics.tasksPending')} value={`${kpi.tasks_pending || 0}`} unit="" color="purple" />
      </div>

      {/* Cost by Field — simple bar chart */}
      {charts.cost_by_field?.length > 0 && (
        <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4">
          <h4 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-3">{t('analytics.costByField')}</h4>
          <div className="space-y-2">
            {charts.cost_by_field.slice(0, 8).map((f, i) => {
              const maxCost = Math.max(...charts.cost_by_field.map(x => x.cost_per_ha || 0), 1);
              const pct = ((f.cost_per_ha || 0) / maxCost) * 100;
              return (
                <div key={i} className="space-y-0.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--aif-text-primary)] truncate">{f.field}</span>
                    <span className="text-[var(--aif-text-muted)] flex-shrink-0 ml-2">{f.cost_per_ha} EUR/ha</span>
                  </div>
                  <div className="h-2 bg-[var(--aif-bg-elevated)] rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--aif-color-primary)] rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cost by Type */}
      {charts.cost_by_type?.length > 0 && (
        <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4">
          <h4 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-3">{t('analytics.costByType')}</h4>
          <div className="space-y-1.5">
            {charts.cost_by_type.map((c, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="text-[var(--aif-text-secondary)]">{c.type}</span>
                <span className="text-[var(--aif-text-primary)] font-medium">{c.total.toLocaleString('de-DE')} EUR</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Machine Usage */}
      {charts.machine_hours?.length > 0 && (
        <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4">
          <h4 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-3">{t('analytics.machineUsage')}</h4>
          <div className="space-y-1.5">
            {charts.machine_hours.map((m, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="text-[var(--aif-text-secondary)]">{m.machine}</span>
                <span className="text-[var(--aif-text-primary)]">{m.hours_used}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Activities */}
      {charts.monthly_activities?.length > 0 && (
        <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4">
          <h4 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-3">{t('analytics.monthlyActivity')}</h4>
          <div className="flex items-end gap-1 h-20">
            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
              const entry = charts.monthly_activities.find(m => m.month === month);
              const count = entry?.count || 0;
              const maxCount = Math.max(...charts.monthly_activities.map(m => m.count), 1);
              const height = count > 0 ? Math.max((count / maxCount) * 100, 10) : 5;
              return (
                <div key={month} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className={`w-full rounded-t ${count > 0 ? 'bg-[var(--aif-color-primary)]' : 'bg-[var(--aif-bg-elevated)]'}`} style={{ height: `${height}%` }} />
                  <span className="text-[8px] text-[var(--aif-text-muted)]">{month}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {insights.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('analytics.insights')}</h4>
          {insights.map((ins, i) => (
            <div key={i} className={`bg-[var(--aif-bg-surface)] border rounded-xl p-3 ${
              ins.severity === 'warning' ? 'border-amber-300' : 'border-[var(--aif-border-default)]'
            }`}>
              <div className="flex items-start gap-2">
                <span className="text-sm flex-shrink-0">{ins.severity === 'warning' ? '\u26A0\uFE0F' : '\uD83D\uDCA1'}</span>
                <p className="text-xs text-[var(--aif-text-secondary)]">{ins.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, unit, color }) {
  const colors = {
    green: 'text-green-600', amber: 'text-amber-600',
    blue: 'text-blue-600', purple: 'text-purple-600',
  };
  return (
    <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-3 text-center">
      <div className={`text-xl font-bold ${colors[color] || 'text-[var(--aif-text-primary)]'}`}>{value}</div>
      <div className="text-[10px] text-[var(--aif-text-muted)]">{unit && <span className="font-medium">{unit} </span>}{label}</div>
    </div>
  );
}
